import React from 'react';
import { Link } from 'gatsby';
import Helmet from 'react-helmet';
import '@fontsource/inter';
import "@fontsource/inter/200.css"
import "@fontsource/inter/600.css"
import Layout from '../../components/Layout';

import {
  goToGetAmbassador,
  goToCloudPreview,
  goToTelepresenceQuickStart,
  goToContactUs,
  goToTelepresence,
  goToEdgeStack,
  goToServiceCatalogProduct,
  goToGithubAmbassador,
  goTolatestDoc,
  goToTutorialsGettingStarted,
  goToGithubTelepresence
} from '../../utils/routes';

import Arrow from '../../../static/images/use-case/arrow.inline.svg';
import Github from '../../../static/images/homePage/icons/github.inline.svg';
import News from '../../../static/images/homePage/icons/news.inline.svg';
import Outline from '../../../static/images/homePage/icons/outline.inline.svg';
import Edgy from '../../../static/images/homePage/icons/edgy.inline.svg';
import Quote from '../../../static/images/homePage/icons/Quote.inline.svg';
import AppDirect from '../../../static/images/homePage/AppDirect.inline.svg';
import ChickFilA from '../../../static/images/homePage/Chick-fil-A.inline.svg';
import Greenlight from '../../../static/images/homePage/greenlight.inline.svg';
import Microsoft from '../../../static/images/homePage/Microsoft.inline.svg';
import Nvidia from '../../../static/images/homePage/nvidia.inline.svg';
import Ptc from '../../../static/images/homePage/ptc.inline.svg';
import Ticketmaster from '../../../static/images/homePage/ticketmaster.inline.svg';
import Telepresence from '../../../static/images/homePage/telepresence.inline.svg';
import EdgeStack from '../../../static/images/homePage/edgestack.inline.svg';
import Ironclad from '../../../static/images/homePage/Ironclad.inline.svg';
import Newspaper from '../../../static/images/homePage/newspaper.inline.svg';
import Gartner from '../../../static/images/homePage/gartner.inline.svg';
import Kubernetes from '../../../static/images/homePage/kubernetes.inline.svg';

import './home.less';

function HomePage({ location }) {

  return (
    <Layout location={location} >
      <Helmet>
        <title>Ambassador | Ship software faster on Kubernetes</title>
        <meta name="description" content="Ambassador includes Edge Stack (a Kubernetes API Gateway built on Envoy Proxy), Telepresence for fast, local Kubernetes development, and a Service Catalog for Kubernetes annotations." />
        <meta name="keywords" content="Kubernetes, Service Catalog, Telepresence, API Gateway, Microservices, shipping" />
        <meta name="og:type" content="article" />
        <meta name="author" content="Datawire.io" />
      </Helmet>
      <div className="home">
        <section className="home__banner">
          <div className="home__container">
            <h1 className="home__text-primary home__text-primary--main">
              Ship software&nbsp;
              <span className="home__color-green">
                faster
              </span>
              <br className="desktop" />
              &nbsp;on Kubernetes
            </h1>
            <p className="home__text-secondary home__text-secondary--main">
              Thousands of organizations of all sizes, from startups to Fortune 500 companies, <br className="desktop" />
              use Ambassador to code and deliver microservices on Kubernetes faster.
            </p>
            <div className="home__banner__buttons">
              <Link
                className="home__button-primary"
                to={goToCloudPreview}
                target="_blank">
                Get started free
              </Link>
              <Link
                className="home__button-secondary home__button-secondary--centered"
                to={goToContactUs}>
                Get a Demo <Arrow />
              </Link>
            </div>
          </div>

          <div className="home__brands">
            <div className="home__container home__brands-container">
              <Nvidia className="home__brand-icon" />
              <AppDirect className="home__brand-icon" />
              <Microsoft className="home__brand-icon" />
              <Ptc className="home__brand-icon" />
              <Ticketmaster className="home__brand-icon" />
              <ChickFilA className="home__brand-icon" />
              <Greenlight className="home__brand-icon home__brand-icon-greenlight" />
            </div>
          </div>
        </section>

        <section className="home__container home__code">
          <h1 className="home__code__title">
            <span className="home__color-yellow">Find</span>.&nbsp;
            <span className="home__color-red">Fix</span>.&nbsp;
            <span className="home__color-green">Release</span>.
          </h1>
          <p className="home__text-secondary">
            Our software enables application developers on Kubernetes to
            <br className="desktop" />
            find critical information, fix issues rapidly, and safely release code to end users,
            <br className="desktop" />
            without requiring an overworked DevOps engineer for support.
          </p>
          <Edgy className="home__code__edgy" />
        </section>

        <hr className="home__hr" />

        <section className="home__cardInfo home__container">
          <div className="home__cardInfo__info">
            <div className="home__cardInfo__info--title">
              <Newspaper className="home__headeline-icon" />
              <h2>
                Service Catalog
              </h2>
            </div>
            <h3 className="home__cardInfo-subtitle home__color-blue">
              Speed incident response
            </h3>
            <p className="home__text-secondary home__text-secondary-servicecatalog">
                Find critical information about your microservices. Who owns the service? Where’s the source repository?
                Use Kubernetes annotations to document your services and share this information with your team in our portal.
            </p>
            <div className="home__container--buttons">
              <Link
                className="home__button-line home__button-line--blue"
                to={goToGetAmbassador}>
                <span>get started</span>
              </Link>
              <Link
                className="home__button-secondary home__button-secondary--blue"
                to={goToServiceCatalogProduct}>
                learn more
                  <Arrow />
              </Link>
            </div>
          </div>
          <div className="home__card  home__card--blue">
            <Quote className="quote" />
            <p>
              The ease of use of a portal, the completeness of the API documentation, the ease with which you can search for the right
              API and the speed at which developers can find and try out the API they need, are all fundamental to the success of an
              API program.
            </p>
            <Gartner className="home__icon-gartner" />
          </div>
        </section>

        <section className="home__cardInfo home__container">
          <div className="home__cardInfo__info">
            <div className="home__cardInfo__info--title">
              <Telepresence className="home__headeline-icon" />
              <h2>
                Telepresence
              </h2>
            </div>
            <h3 className="home__cardInfo-subtitle home__color-purple">
              Fast, local development for Kubernetes microservices
            </h3>
            <p className="home__text-secondary">
              Code a single microservice locally while running the rest
              of your application in the cloud. Use your favorite IDE and
              tools to accelerate your inner dev loop.
            </p>
            <div className="home__container--buttons">
              <Link
                className="home__button-line home__button-line--purple"
                to={goToTelepresenceQuickStart}
                target="_blank">
                <span>get started</span>
              </Link>
              <Link
                className="home__button-secondary home__button-secondary--purple"
                to={goToTelepresence}>
                <span>Learn more</span>
                <Arrow />
              </Link>
            </div>
          </div>
          <div className="home__card home__card--purple">
            <Quote className="quote" />
            <p>Telepresence provides a quick and easy way to get a full stack running locally, and each service’s development build can be fast and simple.</p>
            <Ironclad />
          </div>
        </section>

        <section className="home__cardInfo home__container">
          <div className="home__cardInfo__info">
            <div className="home__cardInfo__info--title">
              <EdgeStack className="home__headeline-icon" />
              <h2>
                Edge Stack
              </h2>
            </div>
            <h3 className="home__cardInfo-subtitle home__color-turquoise">
              Self-service, Kubernetes-native API Gateway
            </h3>
            <p className="home__text-secondary">
              Secure your microservices at the Edge. Leverage the power of
              Envoy Proxy to load balance, authenticate, rate limit, and route traffic into your Kubernetes cluster.
              Use declarative CRDs to manage Edge Stack, integrating it seamlessly into your GitOps workflow.
            </p>
            <div className="home__container--buttons">
              <Link
                className="home__button-line home__button-line--turquoise"
                to={goToTutorialsGettingStarted}>
                <span>get started</span>
              </Link>
              <Link
                className="home__button-secondary home__button-secondary--turquoise"
                to={goToEdgeStack}>
                <span>Learn more</span> <Arrow />
              </Link>
            </div>
          </div>
          <div className="home__card home__card--green">
            <Quote className="quote" />
            <p>Ambassador provided an easy path forward at Greenlight for Kubernetes adoption and empowering self sufficient engineering teams. The first class support we received in addition to their rock solid engineering gives us great confidence in our partnership.</p>
            <Greenlight className="home__icon-greenlight" />
          </div>
        </section>

        <hr className="home__hr" />

        <section className="home__container home__reasons">
          <h2 className="home__title-reason">
            Why Ambassador?
          </h2>
          <div className="home__reasons-container">
            <div className="home__reason">
              <h3 className="home__reason-title">
                community
              </h3>
              <p className="home__reason-description">
                We have thousands of users on our Slack channel. Hundreds of developers have contributed to our open source projects, Ambassador and Telepresence.
                </p>
              <div class="home__container--buttons">
                <Link
                  className="home__button-smallicon"
                  to={goToGithubAmbassador}>
                  <Github className="home__icon-github" />
                  ambassador
                </Link>
                <Link
                  className="home__button-smallicon"
                  to={goToGithubTelepresence}>
                  <Github className="home__icon-github" />
                    telepresence
                  </Link>
              </div>
            </div>
            <div className="home__reason">
              <h3 className="home__reason-title">
                designed for developers
              </h3>
              <p className="home__reason-description">
                We agonize over how to optimize the cloud-native developer workflow, so you can focus on writing and shipping code.
              </p>
              <div class="home__container--buttons">
                <Link
                  className="home__button-smallicon"
                  to={goTolatestDoc}>
                  <News className="home__icon-news" />
                    read the docs
                  </Link>
              </div>
            </div>
            <div className="home__reason">
              <h3 className="home__reason-title">
                kubernetes first
              </h3>
              <p className="home__reason-description">
                Our software doesn’t run anywhere outside of Kubernetes. This lets us take full advantage of Kubernetes, and build software that is fully idiomatic with the Kubernetes ecosystem.
              </p>
              <Kubernetes className="home__icon-kubernetes" />
            </div>
            <div className="home__reason">
              <h3 className="home__reason-title">
                deliberately boring
              </h3>
              <p className="home__reason-description">
                Our software is deployed in environments that peak at over 100K requests per second, and backed by our 24x7 support and engineering teams. We conduct regular gamedays and postmortem every outage so that we’re constantly improving.
              </p>
              <Edgy className="home__icon-edgy" />
            </div>
          </div>
        </section>

        <hr className="home__hr-nomargin" />

        <section className="home__container home__ready">
          <h2 className="home__ready-title">
            Ready to ship Kubernetes services faster?
          </h2>
          <h3 className="home__ready-description">
            Start using Telepresence to set up a
            <br />
            real-time development loop in minutes.
          </h3>
        </section>

        <h3 className="home__text-never-go-back">
          You’ll never go back.
        </h3>

        <section className="home__container home__container-buttons home__container-buttons--center">
          <Link
            className="home__button-primary"
            to={goToCloudPreview}
            target="_blank">
            start now
          </Link>
          <Link
            className="home__button-outline"
            to={goToContactUs}>
            <Outline className="home__icon-outline" />
            contact sales
          </Link>
        </section>
      </div>
    </Layout >
  );
}

export default HomePage;
